import { ref, reactive, computed } from 'vue';
import { C_REG_MERGE, 
    emailRegex, 
    REG_FIELD, 
    REGISTRATION_FIELD_ERROR, 
    validPhoneNumberRegex, 
    validZipCodeRegex 
} from '_acaSrc/utility/constants';
import { validatePassword, validateUserName } from '_acaSrc/utility/Validators';
import { useStore } from 'vuex';

let formInstance; // Singleton instance

export const REG_FORM_INITIAL = {
    salutation: null,
    firstName: '',
    lastName: '',
    suffix: '',
    address1: '',
    address2: '',
    email: '',
    phone1: '',
    phone1Type: '',
    phone1Ext: '',
    country: null,
    zip: '',
    city: '',
    county: '',
    state: null,
    practiceSetting: null,
    practiceType: null,
    specialty: null,
    registrationUserName: '',
    password: '',
    verifyPassword: '',
    verificationCode: '',
    termsAccepted: false
};

export const REG_FORM_SERVER_ERROR_INITIAL = {
    general: '',
    firstName: '',
    lastName: '',
    email: '',
    city: '',
    zip: '',
    address1: '',
    phone1: '',
    registrationUserName: '',
    password: '',
    verifyPassword: '',
    verificationCode: ''
};

export const REG_FORM_IS_DIRTY_INITIAL = {
    firstName: false,
    lastName: false,
    suffix: false,
    address1: false,
    address2: false,
    phone1: false,
    country: false,
    email: false,
    zip: false,
    city: false,        
    registrationUserName: false,
    password: false,
    verifyPassword: false,
    verificationCode: false,
    termsAccepted: false
};

export default function useRegistrationForm() {
    if (formInstance) {
        return formInstance;
    }
    const form = reactive({ ...REG_FORM_INITIAL });

    const serverError = reactive({ ...REG_FORM_SERVER_ERROR_INITIAL });

    const serverSuccess = reactive({
        isSuccess: false,
        message: '',
        heading: ''
    });
    const cityDropdownOptions = ref([]);
    const filteredStateOptions = ref([]);

    const formSubmitted = ref(false);
    const formReset = ref(false);
    const isDirty = reactive({ ...REG_FORM_IS_DIRTY_INITIAL });

    const resetForm = () => {
        Object.assign(form, REG_FORM_INITIAL);
        resetAllServerErrors();
        formSubmitted.value = false;
        formReset.value = false;
        needsNewCode.value = false;
        Object.keys(isDirty).forEach(key => isDirty[key] = false);
        prefillFormFields();
    };

    const resetServerError = (field) => {
        serverError[field] = '';
    };

    const resetAllServerErrors = () => {
        Object.assign(serverError, REG_FORM_SERVER_ERROR_INITIAL);
    };

    const isSubmitDisabled = ref(true);
    const checkResetServerError = (field) => {
        if (field) {
            resetServerError(field);
        }
        if (canSubmitForm()) {
            isSubmitDisabled.value = false;
        }
        else {
            isSubmitDisabled.value = true;
        }
    };

    const MAX_SEND_CODE_ATTEMPTS = 5;
    const sendCodeAttempts = ref(0);
    const isSendNewCodeDisabled = computed(() => 
        sendCodeAttempts.value >= MAX_SEND_CODE_ATTEMPTS
        || isSubmitDisabled.value
    );

    const store = useStore();
    const registrationForm = computed(() => store?.getters['login/registrationForm'] || {});
    const formFields = computed(() => registrationForm?.value?.formFields || {});

    const countryOptions = computed(() => store?.getters['account/myAccountCountries'] || []);
    const isUSA = computed(() => {
        return countryOptions?.value[form.country]?.value === 'USA';
    });
    const isCanada = computed(() => {
        return countryOptions?.value[form.country]?.value === 'CAN';
    });
    const isUSOrCanada = computed(() => {
        return isUSA.value
            || isCanada.value
            || registrationForm.value.formName === 'DOMESTIC';
    });
    const zipCodePrefilled = ref(false);
    const isHasPreFilledEmail = ref(false);

    const isFormLoaded = ref(false);
    const prefillFormFields = () => {
        if (!formFields.value) {
            return;
        }

        Object.entries(formFields.value).forEach(([ key, field ]) => {
            if (field.value) {
                form[key] = field.value;
                isDirty[key] = true;
                if (key === REG_FIELD.EMAIL) {
                    isHasPreFilledEmail.value = true;
                }
            }
        });
        isFormLoaded.value = true;
    };
    
    const requiredFields = [
        REG_FIELD.FIRST_NAME,
        REG_FIELD.LAST_NAME,
        REG_FIELD.ADDRESS1,
        REG_FIELD.EMAIL,
        REG_FIELD.PHONE1,
        REG_FIELD.ZIP,
        REG_FIELD.CITY,
        REG_FIELD.USER_NAME,
        REG_FIELD.PASSWORD,
        REG_FIELD.VERIFY_PASSWORD
    ];
    const requiredSelects = [
        REG_FIELD.SALUTATION,
        REG_FIELD.COUNTRY,
        REG_FIELD.PRACTICE_SETTING,
        REG_FIELD.PRACTICE_TYPE,
        REG_FIELD.SPECIALTY
    ];

    const canSubmitForm = () => {
        const hasInvalidField = requiredFields.reduce(
            (acc, field) => acc || checkFieldInvalid(field), false
        );
        const hasInvalidSelect = requiredSelects.reduce(
            (acc, field) => acc || checkSelectInvalid(field), false
        );
        return !hasInvalidField && !hasInvalidSelect && form.termsAccepted;
    };

    const setServerError = (field, error) => {
        serverError[field] = error;
    };

    const validateForm = () => {
        formSubmitted.value = true;
        formReset.value = false;
        return canSubmitForm();
    };

    const visibleFields = computed(() => store?.getters['login/visibleRegFormFields'] || []);
    const checkSelectInvalid = (formValue) => {
        return form[formValue] === null
            && visibleFields.value.includes(formValue);
    };

    const invalidPhone1 = () => {
        return (!validPhoneNumberRegex.test(form.phone1.trim())
            && visibleFields.value.includes(REG_FIELD.PHONE1)) 
            || serverError.phone1 !== '';
    };

    const needsNewCode = ref(false);
    const invalidEmail = () => {
        if (needsNewCode.value) {
            return false;
        }
        return !emailRegex.test(form.email)
            || serverError.email !== '';
    };

    const invalidZipCode = () => {
        return ((!validZipCodeRegex.test(form.zip) 
            || form.zip.trim() === '') 
            && isUSOrCanada.value)
            || serverError.zip !== ''
        ;
    };

    const invalidCity = () => {
        return (form.city.trim() === ''
            && visibleFields.value.includes(REG_FIELD.CITY)
            && !isUSA.value)
            || serverError.city !== '';
    };

    const invalidUserName = () => {
        return (validateUserName(form.registrationUserName) !== ''
            || form.registrationUserName.trim() === '')
            && visibleFields.value.includes(REG_FIELD.USER_NAME)
            || serverError.registrationUserName !== '';
    };

    const invalidPassword = () => {
        return (validatePassword(form.password, form.registrationUserName) !== ''
            || form.password.trim() === '')
            && visibleFields.value.includes(REG_FIELD.PASSWORD)
            || serverError.password !== '';
    };

    const invalidVerifyPassword = () => {
        return (form.password !== form.verifyPassword
            || form.verifyPassword.trim() === '')
            && visibleFields.value.includes(REG_FIELD.VERIFY_PASSWORD)
            || serverError.verifyPassword !== '';
    };

    const invalidTerms = () => {
        return !form.termsAccepted;
    };

    const checkFieldInvalid = (formValue) => {
        if (!visibleFields.value.includes(formValue)) {
            return false;
        }
        if (Object.keys(errorFieldMap).includes(formValue)) {
            return handleCustomFieldInvalid(formValue);
        }
        return form[formValue]?.toString().trim() === '' 
            || serverError[formValue] !== '';
    };

    const errorFieldMap = {
        phone1: invalidPhone1,
        email: invalidEmail,
        zip: invalidZipCode,
        city: invalidCity,
        registrationUserName: invalidUserName,
        password: invalidPassword,
        verifyPassword: invalidVerifyPassword,
        termsAccepted: invalidTerms
    };
    const handleCustomFieldInvalid = (formValue) => {
        if (errorFieldMap[formValue]) {
            return errorFieldMap[formValue]();
        }
    };

    const getFieldErrorMessage = (field) => {
        if (serverError[field]) {
            return serverError[field];
        }
        if (checkFieldInvalid(field)) {
            return REGISTRATION_FIELD_ERROR[field.toUpperCase()] || '';
        }
        return '';
    };

    const userNameErrorMessage = () => {
        if (serverError.registrationUserName) {
            return serverError.registrationUserName;
        }
        return validateUserName(form.registrationUserName);
    };

    const passwordErrorMessage = () => {
        if (serverError.password) {
            return serverError.password;
        }
        return validatePassword(form.password, form.registrationUserName);
    };

    const invalidVerificationCode = () => {
        return form.verificationCode.trim() === '' || serverError.verificationCode !== '';
    };

    const verificationCodeErrorMessage = () => {
        if (serverError.verificationCode) {
            return serverError.verificationCode;
        }
        if (invalidVerificationCode()) {
            return REGISTRATION_FIELD_ERROR.VERIFICATIONCODE;
        }
        return '';
    };

    const successMessage = () => {
        if (!serverSuccess.isSuccess) {
            return C_REG_MERGE.UNEXPECTED_ERROR;
        }
        return serverSuccess;
    };

    const setSuccessMessage = (success) => {
        if (success) {
            serverSuccess.isSuccess = true;
            serverSuccess.message = success.message;
            serverSuccess.heading = success.heading;
        }
    };

    const clearSuccessMessage = () => {
        serverSuccess.isSuccess = false;
        serverSuccess.message = '';
        serverSuccess.heading = '';
    };

    formInstance = {
        form,
        serverError,
        serverSuccess,
        formSubmitted,
        formReset,
        isFormLoaded,
        isDirty,
        isUSA,
        isCanada,
        isUSOrCanada,
        isSubmitDisabled,
        isSendNewCodeDisabled,
        sendCodeAttempts,
        needsNewCode,
        zipCodePrefilled,
        cityDropdownOptions,
        filteredStateOptions,
        prefillFormFields,
        checkResetServerError,
        setServerError,
        resetForm,
        resetServerError,
        resetAllServerErrors,
        canSubmitForm,
        validateForm,
        checkFieldInvalid,
        checkSelectInvalid,
        getFieldErrorMessage,
        invalidVerificationCode,
        verificationCodeErrorMessage,
        userNameErrorMessage,
        passwordErrorMessage,
        successMessage,
        setSuccessMessage,
        clearSuccessMessage,
        isHasPreFilledEmail
    };
    return formInstance;
}